/**
 * Pure helpers for the Leadership report page (8 Oct). Import-free, so
 * `tests/leadership-report.test.mjs` can load them directly.
 */

/** The HelpDesk manager overview's report name on the server. */
export const HELPDESK_OVERVIEW = "helpdesk-overview";

/**
 * The report frame's sandbox: its own scripts run, but in an opaque origin
 * (no `allow-same-origin`), so it cannot read the app's cookies, storage or
 * DOM, call the API as the viewer, open windows or navigate the app.
 */
export const REPORT_FRAME_SANDBOX = "allow-scripts";

/** Longest note the server takes ("Add a note", 16 Oct). */
export const COMMENT_MAX = 2000;

/** Whether a draft note can be sent: something besides spaces, not too long. */
export function canSendComment(text: string): boolean {
  const trimmed = text.trim();
  return trimmed.length > 0 && trimmed.length <= COMMENT_MAX;
}

export interface SnapshotLike {
  id: number;
  generated_at: string;
}

/**
 * The date picker's options, newest first, the newest marked as latest.
 *
 * `format` renders the date; `latest` wraps the newest one's label.
 */
export function snapshotOptions(
  items: readonly SnapshotLike[],
  format: (iso: string) => string,
  latest: (label: string) => string,
): { value: string; label: string }[] {
  return items.map((item, index) => {
    const label = format(item.generated_at);
    return { value: String(item.id), label: index === 0 ? latest(label) : label };
  });
}

/** The snapshot to show: the chosen one while it still exists, else the newest. */
export function pickSnapshotId(
  items: readonly SnapshotLike[],
  chosen: number | null,
): number | null {
  if (chosen !== null && items.some((item) => item.id === chosen)) {
    return chosen;
  }
  return items[0]?.id ?? null;
}

/**
 * A time as the report states it: New York time, "09 Oct 14:05 ET" (the
 * report's days are New York days, Rusty's local time). `null` for no time.
 */
export function etTime(iso: string | null | undefined, locale = "en-GB"): string | null {
  if (!iso) {
    return null;
  }
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) {
    return null;
  }
  const text = new Intl.DateTimeFormat(locale, {
    timeZone: "America/New_York",
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(at);
  return `${text} ET`;
}

/** "HH:MM" in a time zone (24 h), or null for no time. */
export function clockTime(
  iso: string | null | undefined,
  timeZone: string,
  locale = "en-GB",
): string | null {
  if (!iso) {
    return null;
  }
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) {
    return null;
  }
  return new Intl.DateTimeFormat(locale, {
    timeZone,
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(at);
}

/** The New York calendar day of a time, "YYYY-MM-DD". */
function nyDay(at: Date): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "America/New_York" }).format(at);
}

/**
 * "Data pulled …" time (12 Oct): "HH:MM ET" when it is today in New York,
 * else with the date ("09 Oct 14:05 ET"), so an old pull is never mistaken
 * for today's.
 */
export function pulledTime(
  iso: string | null | undefined,
  locale = "en-GB",
  now: Date = new Date(),
): string | null {
  if (!iso) {
    return null;
  }
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) {
    return null;
  }
  if (nyDay(at) === nyDay(now)) {
    return `${clockTime(iso, "America/New_York", locale)} ET`;
  }
  return etTime(iso, locale);
}

/** The next slot as the page states it: "12:00 ET" and "18:00" (Denmark). */
export function nextUpdateTimes(
  iso: string | null | undefined,
  locale = "en-GB",
): { et: string; dk: string } | null {
  const et = clockTime(iso, "America/New_York", locale);
  const dk = clockTime(iso, "Europe/Copenhagen", locale);
  return et && dk ? { et: `${et} ET`, dk } : null;
}

/**
 * "Update now" (12 Oct): only for Rusty (the server says so through
 * `can_update_now`; it also refuses everyone else). Hidden — not disabled —
 * for every other viewer.
 */
export function showUpdateNow(status: { can_update_now?: boolean | null } | undefined): boolean {
  return status?.can_update_now === true;
}

/** Whether "Update now" may be pressed now: not running, and the 15-minute wait is over. */
export function updateNowReady(
  status: { running?: boolean | null; update_now_available_at?: string | null } | undefined,
  now: Date = new Date(),
): boolean {
  if (!status || status.running === true) {
    return false;
  }
  if (!status.update_now_available_at) {
    return true;
  }
  return new Date(status.update_now_available_at) <= now;
}

/**
 * Whether the page should say the last refresh failed: a failure newer than
 * the last success (the server already drops an older one).
 */
export function refreshFailed(status: {
  last_failure_at?: string | null;
  last_success_at?: string | null;
}): boolean {
  if (!status.last_failure_at) {
    return false;
  }
  if (!status.last_success_at) {
    return true;
  }
  return new Date(status.last_failure_at) > new Date(status.last_success_at);
}
